import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../services/api";

const DAY_NAMES = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];
const MONTH_NAMES = [
    "Január","Február","Marec","Apríl","Máj","Jún",
    "Júl","August","September","Október","November","December"
];

function toISO(date) {
    return date.toISOString().slice(0, 10);
}

function parseISO(str) {
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
}

function isBetween(date, start, end) {
    const d = toISO(date);
    return d >= toISO(start) && d <= toISO(end);
}

function isDateBooked(date, bookedRanges) {
    const d = toISO(date);
    return bookedRanges.some(r => d >= r.start && d <= r.end);
}

function buildMonthGrid(year, month) {
    const firstDay = new Date(year, month, 1);
    const lastDay  = new Date(year, month + 1, 0);
    // Monday-first: getDay() 0=Sun → push to 6, 1=Mon→0, etc.
    const startPad = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let p = 0; p < startPad; p++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
        days.push(new Date(year, month, d));
    }
    while (days.length % 7 !== 0) days.push(null);
    return days;
}

// ── Single month view ─────────────────────────────────────────────────────────
function MonthGrid({ year, month, startDate, endDate, hoveredDate, bookedRanges, onDayClick, onDayHover }) {
    const today = new Date(); today.setHours(0,0,0,0);
    const days  = buildMonthGrid(year, month);

    return (
        <div className="flex-1 min-w-0">
            <div className="text-center font-semibold text-white text-sm mb-3">
                {MONTH_NAMES[month]} {year}
            </div>
            <div className="grid grid-cols-7 mb-1">
                {DAY_NAMES.map(d => (
                    <div key={d} className="text-center text-[10px] font-mono uppercase tracking-wider text-zinc-600 py-1">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
                {days.map((day, i) => {
                    if (!day) return <div key={`pad-${i}`} />;
                    const iso     = toISO(day);
                    const isPast  = day < today;
                    const booked  = isDateBooked(day, bookedRanges);
                    const isStart = startDate && toISO(startDate) === iso;
                    const isEnd   = endDate   && toISO(endDate)   === iso;
                    const rangeEnd = endDate || hoveredDate;
                    const inRange = startDate && rangeEnd && !isStart && !isEnd &&
                        ((endDate   && isBetween(day, startDate, endDate)) ||
                         (!endDate  && hoveredDate && isBetween(day, startDate, hoveredDate) && day > startDate));
                    const disabled = isPast || booked;

                    let cls = "relative h-8 w-full text-xs font-medium transition-colors select-none flex items-center justify-center rounded-sm ";
                    if (disabled) {
                        cls += booked
                            ? "text-zinc-700 cursor-not-allowed line-through"
                            : "text-zinc-700 cursor-not-allowed";
                    } else if (isStart || isEnd) {
                        cls += "bg-nova-cyan text-black font-bold cursor-pointer rounded-md z-10";
                    } else if (inRange) {
                        cls += "bg-nova-cyan/20 text-nova-cyan cursor-pointer";
                    } else {
                        cls += "text-zinc-300 hover:bg-white/8 hover:text-white cursor-pointer";
                    }

                    return (
                        <button
                            key={iso}
                            type="button"
                            disabled={disabled}
                            onClick={() => !disabled && onDayClick(day)}
                            onMouseEnter={() => onDayHover(disabled ? null : day)}
                            className={cls}
                        >
                            {day.getDate()}
                            {booked && <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-zinc-600" />}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ── Main calendar ─────────────────────────────────────────────────────────────
export default function AvailabilityCalendar({ equipmentIds = [], startDate, endDate, onChange }) {
    const today = new Date(); today.setHours(0,0,0,0);
    const [viewYear,  setViewYear]  = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());
    const [bookedRanges, setBooked] = useState([]);
    const [hovered, setHovered]     = useState(null);
    const [loading, setLoading]     = useState(false);

    const idsKey = equipmentIds.join(",");

    // Fetch booked ranges for all equipment IDs
    useEffect(() => {
        if (!idsKey) { setBooked([]); return; }
        const ids = idsKey.split(",").filter(Boolean);
        setLoading(true);
        Promise.all(ids.map(id => api.getEquipmentAvailability(id)))
            .then(results => {
                const ranges = results.flatMap(r => r.booked_ranges || []);
                setBooked(ranges);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);

    const prevMonth = () => {
        if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
        else setViewMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
        else setViewMonth(m => m + 1);
    };

    const month2 = viewMonth === 11 ? 0 : viewMonth + 1;
    const year2  = viewMonth === 11 ? viewYear + 1 : viewYear;

    const handleDayClick = useCallback((day) => {
        if (!startDate || (startDate && endDate)) {
            // Start fresh selection
            onChange({ startDate: day, endDate: null });
        } else {
            // We have startDate, no endDate
            if (day <= startDate) {
                onChange({ startDate: day, endDate: null });
            } else {
                onChange({ startDate, endDate: day });
            }
        }
    }, [startDate, endDate, onChange]);

    const commonProps = { startDate, endDate, hoveredDate: hovered, bookedRanges, onDayClick: handleDayClick, onDayHover: setHovered };

    const canGoPrev = new Date(viewYear, viewMonth, 1) > today;

    return (
        <div className="rounded-xl border border-white/8 bg-nova-black/40 p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <button type="button" onClick={prevMonth} disabled={!canGoPrev}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-white hover:bg-white/8 disabled:opacity-20 transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex-1" />
                <button type="button" onClick={nextMonth}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-white hover:bg-white/8 transition-colors">
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>

            {loading && (
                <p className="text-center text-zinc-600 text-xs font-mono mb-3">Načítavam dostupnosť…</p>
            )}

            <div className="flex gap-6">
                <MonthGrid year={viewYear} month={viewMonth} {...commonProps} />
                <div className="hidden sm:block w-px bg-white/5 shrink-0" />
                <MonthGrid year={year2} month={month2} {...commonProps} />
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-white/5">
                <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-nova-cyan" />
                    <span className="text-[10px] text-zinc-500 font-mono">Vybraný termín</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-zinc-800 relative">
                        <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-zinc-600" />
                    </div>
                    <span className="text-[10px] text-zinc-500 font-mono">Obsadené</span>
                </div>
            </div>
        </div>
    );
}
